import {
  EmptyState,
  FlexBox,
  IconButton,
  Page,
  Table,
} from '@dixa/lib-shared-habla';
import { isResponseWithData } from 'lib-shared-auth';
import { Fragment, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMount } from 'react-use';

import { conversationsApi } from 'apis/conversations';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';

import { notificationActions } from '../../actions/notification';
import {
  AllNotificationsHistoryT,
  getAllNotificationHistory,
} from '../../selectors/notifications';
import { NotificationLayout } from '../notification/notificationLayout';

import { NotificationBulkActionButtons } from './components/notificationBulkActionButtons';
import styles from './notificationsPage.module.scss';

export const NotificationsPage = (): JSX.Element => {
  const dispatch = useAppDispatch();
  const allNotifications = useAppSelector(getAllNotificationHistory);

  const [currentNotification, setCurrentNotification] = useState<string>();

  const [
    readConversationBulkActionFailedActions,
    { failedActions, isFetching },
  ] = conversationsApi.useLazyReadConversationBulkActionFailedActionsQuery({
    selectFromResult: ({ data, ...rest }) => ({
      ...rest,
      failedActions: data?.data.failed_actions ?? [],
    }),
  });

  useMount(() => {
    dispatch(notificationActions.notificationGetPersisted());
  });

  const handleToggleOpenNotification = async (
    notification: AllNotificationsHistoryT,
  ) => {
    if (!notification.bulkId) return;

    if (currentNotification === notification.id) {
      setCurrentNotification(undefined);
      return;
    }

    try {
      const response = await readConversationBulkActionFailedActions(
        { bulkId: notification.bulkId },
        true,
      ).unwrap();

      if (isResponseWithData(response)) {
        setCurrentNotification(notification.id);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const renderNotification = (notification: AllNotificationsHistoryT) => {
    const isOpen = currentNotification === notification.id;
    const { bulkStatus, bulkId } = notification;
    const isBulkAction = !!bulkId;
    const isBulkActionWithFailedActions =
      isBulkAction &&
      (bulkStatus === 'BulkActionFailed' ||
        bulkStatus === 'BulkActionPartialFailed');

    return (
      <Fragment key={notification.id}>
        <Table.Tr>
          <Table.Td>
            <NotificationLayout notification={notification.data} />
          </Table.Td>

          <Table.Td>{notification.message}</Table.Td>

          <Table.Td width="0">
            {isBulkAction && (
              <NotificationBulkActionButtons notification={notification} />
            )}
          </Table.Td>

          <Table.Td width="0">
            {isBulkActionWithFailedActions && (
              <IconButton
                variant="tertiary"
                icon={isOpen ? 'angle-down' : 'angle-right'}
                isLoading={isFetching}
                onClick={() => handleToggleOpenNotification(notification)}
              />
            )}
          </Table.Td>
        </Table.Tr>

        {isOpen && bulkId && (
          <Table.Tr>
            <Table.Td colSpan={4} className={styles.foldCell}>
              <Table framed={false}>
                <Table.Tbody>
                  {failedActions.map((failedAction, i) => (
                    <Table.Tr key={i}>
                      <Table.Td colSpan={4}>
                        <Link
                          to={`/conversation/${failedAction.conversation_id}`}
                        >
                          #{failedAction.conversation_id}
                        </Link>{' '}
                        {failedAction.error_message}
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.Td>
          </Table.Tr>
        )}
      </Fragment>
    );
  };

  const hasNotifications = !!allNotifications.length;

  return (
    <Page>
      <Page.Head title="Notifications" />

      <Page.Body>
        <Page.Section>
          {hasNotifications && (
            <Table>
              <Table.Tbody>
                {allNotifications.map(renderNotification)}
              </Table.Tbody>
            </Table>
          )}

          {!hasNotifications && (
            <FlexBox
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              flex="1"
              height="100%"
            >
              <EmptyState
                title="No notifications"
                description="You are all caught up."
                icon="bell"
              />
            </FlexBox>
          )}
        </Page.Section>
      </Page.Body>
    </Page>
  );
};
